import { useCallback, useMemo } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash-es/isEmpty';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { dialogsEndpoints as endpoints } from '@/api/endpoints';
import { ApiError, callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { createEntityCache } from '@/hooks/utils/entityCache';
import { getRandomElement, randId } from '@/redux/dialogs/helper';
import {
  getActionsSelector,
  noSystemVariables,
  selectAllVariables,
  selectCustomVariables,
  selectDraftDialog,
} from '@/redux/dialogs/selectors';
import { popModal } from '@/redux/modals/actions';
import {
  saveDialog as onDialogSave,
  importDialog as importNodes,
  clearDialogNodes,
  setDialog,
} from '@/redux/nodes/actions';
import { selectAccountSlug, selectDialogId } from '@/redux/session/selectors';
import { Dialog, Dialogs, PartialDialog } from '@/shared/types/dialog';
import { Node, UsedNode } from '@/shared/types/node';
import { RootState } from '@/shared/types/state';
import { exportDialog as downloadDialog, importDialog as uploadDialog } from '@/util/fileManager';
import { clearNewDialog, sameCollection } from '@/util/util';

import { EntityDraft } from './useEntities';
import useWebhooks from './useWebhooks';

// ============================================================================
// TYPES
// ============================================================================

type CollectionsUpdateProps = { collection: string; new_collection: string };

export type ConnectedDialog = { url: string; label: string };

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * API functions for dialog operations
 */
export const API = Object.freeze({
  listDialogs: async (brainId: string): Promise<Dialogs> => callGet(endpoints.dialogs(brainId)),

  getDialog: async (brainId: string, dialogId: string): Promise<Dialog> =>
    callGet(endpoints.dialog(brainId, dialogId)),

  createDialog: async ({
    brainId,
    dialog,
  }: {
    brainId: string;
    dialog: Partial<Dialog>;
  }): Promise<Dialog> => callPost(endpoints.dialogs(brainId), dialog),

  updateCollection: async (brain_id: string, dialog: CollectionsUpdateProps): Promise<Dialogs> =>
    callPut(endpoints.collection(brain_id), dialog),

  updateDialog: async (brainId: string, { dialog_id, ...dialog }: PartialDialog): Promise<Dialog> =>
    callPut(endpoints.dialog(brainId, dialog_id), dialog),

  deleteDialog: async ({
    brainId,
    dialogId,
  }: {
    brainId: string;
    dialogId: string;
  }): Promise<Dialog> => callDelete(endpoints.dialog(brainId, dialogId)),
});

// ============================================================================
// QUERY CACHE HELPERS
// ============================================================================

export const dialogCache = createEntityCache<Dialog, 'dialogs'>({
  listKey: (brainId) => [endpoints.dialogs(brainId)],
  detailKey: (brainId, dialogId) => [endpoints.dialog(brainId, dialogId)],
  listField: 'dialogs',
  getId: (dialog) => dialog.dialog_id,
  getParentId: (dialog) => dialog.brain_id,
});

/**
 * Updates query cache when a dialog collection is updated
 * @param queryClient - TanStack Query client instance
 * @param brainId - Brain ID
 * @param oldCollectionName - Previous collection name
 * @param newCollectionName - New collection name
 */
export const onDialogCollectionUpdated = (
  queryClient: QueryClient,
  brainId: string,
  oldCollectionName: string,
  newCollectionName: string
) => {
  queryClient.setQueryData<Dialogs>([endpoints.dialogs(brainId)], (prev: Dialogs) => ({
    dialogs: prev?.dialogs.map((item) => {
      if (item.collection === oldCollectionName) {
        return { ...item, collection: newCollectionName };
      }
      return item;
    }),
  }));
};

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Custom hook for managing dialogs and related operations
 * @param brainId - Optional brain ID
 * @param dialogId - Optional dialog ID
 * @param nodeId - Optional node ID
 * @returns Dialog management functions and state
 */
const useDialogs = (brainId?: string, dialogId?: string, nodeId?: string) => {
  // ============================================================================
  // HOOKS & STATE
  // ============================================================================

  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { webhooks } = useWebhooks(brainId);
  const { dialogId: paramsDialogId } = useParams();
  const isDraft = paramsDialogId === 'draft';

  const sessionDialogId = useSelector(selectDialogId);
  const dialogDraft: PartialDialog = useSelector(
    (state: RootState) => selectDraftDialog(state),
    shallowEqual
  );
  const slug = useSelector(selectAccountSlug);
  const queryClient = useQueryClient();

  // ============================================================================
  // QUERIES
  // ============================================================================

  const { data: dialogs, status: listStatus } = useQuery<Dialogs, Error>({
    queryKey: [endpoints.dialogs(brainId)],
    queryFn: () => API.listDialogs(brainId),
    enabled: !!brainId,
  });

  const { data: dialog, status: getStatus } = useQuery<Dialog, Error>({
    queryKey: [endpoints.dialog(brainId, dialogId)],
    queryFn: () => API.getDialog(brainId, dialogId),
    enabled: !!brainId && !!dialogId && !isDraft,
  });

  // Computed values
  const dialogName = useMemo(() => dialog?.name, [dialog]);

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  const { mutate: updateDialog, status: updateStatus } = useMutation<Dialog, Error, PartialDialog>({
    mutationFn: (data) => API.updateDialog(brainId, data),
    onSuccess: (resp, variables) => {
      const isDialogRename = !('nodes' in variables);
      if (!isDialogRename) {
        dispatch(onDialogSave());
      }

      dialogCache.updated(
        queryClient,
        { ...resp, dialog_id: variables.dialog_id },
        { parentId: brainId }
      );
    },
    onError: (error: ApiError) => {
      if (error.code === 'trigger_not_found') {
        toast.error(t('dialog.cannot_save_stale_trigger'));
      } else {
        toast.error(error.message);
      }
    },
  });

  const {
    mutate: createDialog,
    mutateAsync: createDialogAsync,
    status: createStatus,
  } = useMutation<Dialog, Error, { dialog: Partial<Dialog>; shouldNavigate?: boolean }>({
    mutationFn: (data) => API.createDialog({ brainId, dialog: data.dialog }),
    onSuccess: (resp, { shouldNavigate = true }) => {
      dispatch(setDialog(resp));
      dialogCache.created(queryClient, resp, { parentId: brainId });

      if (shouldNavigate) {
        setTimeout(() => {
          navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${resp.dialog_id}`);
        }, 0);
      }
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteDialog, status: deleteStatus } = useMutation<Dialog, Error>({
    mutationFn: () => API.deleteDialog({ brainId, dialogId }),
    onSuccess: () => {
      dispatch(popModal());
      const dialogsInCollection = sameCollection('dialogs', 'dialog_id', dialogId, dialogs);
      dispatch(clearDialogNodes());
      dialogCache.removed(queryClient, { parentId: brainId, id: dialogId });

      toast.success(t('dialog.dialog_deleted', { 0: dialogName }));

      const url = `/${slug}/ai-agents/${brainId}/workflows/dialogs`;
      if (dialogsInCollection.length === 0) {
        navigate(url);
      } else {
        navigate(`${url}/${dialogsInCollection[0].dialog_id}`);
      }
    },
    meta: { errorToast: true },
  });

  const { mutate: updateCollection, status: updateCollectionStatus } = useMutation<
    Dialogs,
    Error,
    CollectionsUpdateProps
  >({
    mutationFn: (data) => API.updateCollection(brainId, data),
    onSuccess: (resp, variables) => {
      const newCollectionName = resp.dialogs[0]?.collection;
      onDialogCollectionUpdated(
        queryClient,
        brainId,
        variables.collection,
        variables.new_collection
      );

      toast.success(t('dialog.collection_updated', { 0: newCollectionName }));
    },
  });

  // ============================================================================
  // DIALOG OPERATIONS
  // ============================================================================

  const saveDialog = useCallback(async () => {
    if (isDraft) {
      return new Promise<void>((resolve, reject): void => {
        const previousCollection = dialogs?.dialogs?.find(
          (e) => e.dialog_id === sessionDialogId
        )?.collection;

        const newDialog = { ...dialogDraft, collection: previousCollection };
        createDialog(
          { dialog: newDialog },
          {
            onSuccess: (resp) => {
              resolve();
              toast.success(t('dialog.dialog_updated', { 0: resp.name }));
            },
            onError: (error) => {
              reject(error);
            },
          }
        );
      });
    }
    return new Promise<void>((resolve, reject): void => {
      updateDialog(dialogDraft, {
        onSuccess: (resp) => {
          dispatch(popModal());
          toast.success(t('dialog.dialog_updated', { 0: resp.name }));
          resolve();
        },
        onError: (error) => {
          reject(error);
        },
      });
    });
  }, [
    createDialog,
    dialogDraft,
    dialogs?.dialogs,
    dispatch,
    isDraft,
    sessionDialogId,
    t,
    updateDialog,
  ]);

  const updateDialogName = useCallback(
    async (newDialog?: PartialDialog) => {
      return new Promise<void>((resolve, reject): void => {
        updateDialog(newDialog ?? dialogDraft, {
          onSuccess: (resp) => {
            dispatch(popModal());
            toast.success(t('dialog.dialog_updated', { 0: resp.name }));
            resolve();
          },
          onError: () => {
            reject();
          },
        });
      });
    },
    [dialogDraft, dispatch, t, updateDialog]
  );

  const createDraftDialog = useCallback(() => {
    navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/draft`);
  }, [brainId, slug, navigate]);

  const createCollection = useCallback(() => {
    const newDialog = { name: `Untitled-${randId()}`, collection: `Collection-${randId()}` };
    createDialog({ dialog: newDialog });
  }, [createDialog]);

  const exportDialog = useCallback(async () => {
    const eDialog = await callGet(`/www/api/v1/brains/${brainId}/dialogs/${dialogId}?export=true`);
    downloadDialog(eDialog);
    toast.success(t('dialog.dialog_exported', { 0: eDialog.name }));
  }, [brainId, dialogId, t]);

  const importDialog = useCallback(
    async ({ e, entities, preserveNodeIds = [] }) => {
      uploadDialog(dialog, e.target?.files[0])
        .then((newDialog: { nodes: Node[] }) => {
          clearNewDialog(newDialog, entities);
          dispatch(importNodes({ ...newDialog, preserveNodeIds }));
        })
        .then(() => {
          const name = dialogName ?? t('common.draft');
          toast.success(t('dialog.dialog_imported', { 0: name }));
        })
        .catch((error: Error) => {
          toast.error(error.message);
        });
    },
    [dialog, dialogName, dispatch, t]
  );

  // ============================================================================
  // COMPUTED VALUES & UTILITIES
  // ============================================================================

  const hasHandover = useCallback(() => {
    if (!dialogs?.dialogs) {
      return false;
    } else {
      return getActionsSelector(dialogs.dialogs).some((dialog) => dialog.type === 'handover');
    }
  }, [dialogs]);

  const eventsOptions = useMemo(() => {
    const dialogNodes = (dialogs?.dialogs ?? []).reduce(
      (acc, curr) => [...acc, ...(curr.nodes ?? [])],
      [] as { node_id: string; name: string }[]
    );

    const newNodes = dialogDraft?.nodes?.reduce(
      (acc, curr) => {
        if (dialogNodes?.filter((n) => n.node_id === curr.node_id).length === 0) {
          return [...acc, { node_id: curr.node_id, name: curr.name }];
        }
        return acc;
      },
      [] as { node_id: string; name: string }[]
    );
    const allNodes = dialogNodes?.concat(newNodes ?? []);
    return (
      allNodes?.map(({ node_id, name }) => ({
        value: node_id,
        label: name,
        disabled: nodeId ? node_id === nodeId : false,
      })) ?? []
    );
  }, [dialogDraft?.nodes, dialogs?.dialogs, nodeId]);

  const detailedOptions = useMemo(() => {
    const nodeMap = new Map();

    // Process all dialogs and their nodes - O(n*m)
    dialogs?.dialogs?.forEach((dialog) => {
      dialog.nodes?.forEach((node) => {
        nodeMap.set(node.node_id, {
          dialog_name: dialog.name,
          dialog_id: dialog.dialog_id,
          value: node.node_id,
          label: node.name,
          node_type: node.type,
        });
      });
    });

    // Add draft nodes if they don't already exist - O(k) with O(1) lookups
    dialogDraft?.nodes?.forEach((node) => {
      if (!nodeMap.has(node.node_id)) {
        nodeMap.set(node.node_id, {
          dialog_name: dialogDraft.name,
          value: node.node_id,
          label: node.name,
          node_type: node.type,
        });
      }
    });

    return Array.from(nodeMap.values());
  }, [dialogDraft, dialogs]);

  const dialogTags = useMemo(() => {
    const allTags: Set<string> = new Set();
    dialogs?.dialogs?.forEach((dialog) => {
      dialog?.nodes?.forEach((node) => {
        node?.actions?.forEach((action) => {
          if (action.type === 'tag') {
            action.tags?.forEach((tag) => allTags.add(tag));
          }
        });
        node?.conditions?.forEach((cond) => {
          cond?.actions?.forEach((action) => {
            if (action.type === 'tag') {
              action.tags?.forEach((tag) => allTags.add(tag));
            }
          });
        });
      });
    });
    return allTags;
  }, [dialogs?.dialogs]);

  const dialogNames = useMemo(() => dialogs?.dialogs?.map((i) => i.name.toLowerCase()), [dialogs]);

  const randomNode = useMemo(() => getRandomElement(detailedOptions), [detailedOptions]);

  // ============================================================================
  // VARIABLE & WEBHOOK UTILITIES
  // ============================================================================

  const getContextVariables = useCallback(
    (includeSystemVariables = true) =>
      includeSystemVariables
        ? selectAllVariables(dialogs?.dialogs, dialogDraft, webhooks)
        : noSystemVariables(dialogs?.dialogs, dialogDraft, webhooks),
    [dialogDraft, dialogs, webhooks]
  );

  const getCustomVariables = useCallback(
    () => selectCustomVariables(dialogs?.dialogs, dialogDraft, webhooks),
    [dialogDraft, dialogs, webhooks]
  );

  const dialogsByWebhookId = useMemo(() => {
    const byWebhookId = new Map<string, ConnectedDialog[]>();
    dialogs?.dialogs?.forEach((dialog) => {
      const seen = new Set<string>();
      dialog.nodes?.forEach((node) => {
        const actions = [
          ...(node?.actions ?? []),
          ...(node?.conditions ?? []).flatMap((condition) => condition?.actions ?? []),
        ];
        actions.forEach((action) => {
          if (action?.type !== 'webhook' || !action.webhook_id || seen.has(action.webhook_id)) {
            return;
          }
          seen.add(action.webhook_id);
          const connected = byWebhookId.get(action.webhook_id) ?? [];
          connected.push({ url: dialog.dialog_id, label: dialog.name });
          byWebhookId.set(action.webhook_id, connected);
        });
      });
    });
    return byWebhookId;
  }, [dialogs]);

  const findConnectedDialogsForWebhooks = useCallback(
    (webhook_id: string) => dialogsByWebhookId.get(webhook_id) ?? [],
    [dialogsByWebhookId]
  );

  // ============================================================================
  // DIALOG UPDATE UTILITIES
  // ============================================================================

  const updateIntentNameOnDialogs = async (usedNodes: UsedNode[], payload: { intent: string }) => {
    const usedNodesIdsCollection = [];
    usedNodes.forEach((x) => usedNodesIdsCollection.push(x.nodeId));

    const dialogsToMutate = [];

    dialogs?.dialogs?.forEach((d) =>
      usedNodes.forEach((node) => {
        if (d.dialog_id === node.dialogId && !dialogsToMutate.includes(d)) {
          const mutatedNodes = d.nodes.map((mutatedNode) =>
            usedNodesIdsCollection.includes(mutatedNode.node_id)
              ? { ...mutatedNode, ...payload }
              : mutatedNode
          );
          updateDialog({ dialog_id: d.dialog_id, nodes: mutatedNodes });
        }
      })
    );
  };

  const updateEntityNameAndValuesOnDialogs = useCallback(
    async (old_draft: EntityDraft, new_draft: EntityDraft) => {
      const oldName = `@${old_draft.new_entity}`;
      const newName = `@${new_draft.new_entity}`;
      const oldValues = old_draft.values;
      const newValues = new_draft.values;
      const renamedValues = oldValues.reduce(
        (acc, oldVal) => {
          const sameIdValue = newValues.find((newVal) => newVal?.id === oldVal?.id);
          return sameIdValue?.value !== oldVal?.value
            ? [...acc, { name: newName, old: oldVal?.value, new: sameIdValue?.value }]
            : acc;
        },
        [] as { name: string; old: string; new: string }[]
      );
      if (!isEmpty(renamedValues)) {
        dialogs?.dialogs?.forEach((dialog) => {
          if (!dialog.nodes) {
            return;
          }
          let updateFlag = false;
          const newNodes = dialog.nodes.map((node) => {
            const newConditions = node?.conditions?.map((cond) => {
              const newRules = cond?.rules.map((rule) => {
                const renamed = renamedValues.find(
                  (val) => val?.name === rule?.name && val?.old === rule?.value
                );
                if (renamed) {
                  updateFlag = true;
                  return { ...rule, value: renamed.new };
                }
                return rule;
              });
              return { ...cond, rules: newRules };
            });
            return { ...node, conditions: newConditions };
          });
          if (updateFlag) {
            updateDialog({ dialog_id: dialog.dialog_id, nodes: newNodes });
          }
        });
      }
      if (oldName !== newName) {
        dialogs?.dialogs?.forEach((dialog) => {
          if (!dialog.nodes) {
            return;
          }
          let updateFlag = false;
          const newNodes = dialog.nodes.map((node) => {
            const newRequisites =
              node?.requisites?.map((req) => {
                if (req?.check_for === oldName) {
                  updateFlag = true;
                  return { ...req, check_for: newName };
                }
                return req;
              }) ?? undefined;
            const newConditions =
              node?.conditions?.map((cond) => {
                const newRules = cond.rules.map((rule) => {
                  if (rule?.name === oldName) {
                    updateFlag = true;
                    return { ...rule, name: newName };
                  }
                  return rule;
                });
                return { ...cond, rules: newRules };
              }) ?? undefined;
            return { ...node, requisites: newRequisites, conditions: newConditions };
          });
          if (updateFlag) {
            updateDialog({ dialog_id: dialog.dialog_id, nodes: newNodes });
          }
        });
      }
    },
    [dialogs?.dialogs, updateDialog]
  );

  // ============================================================================
  // RETURN VALUES
  // ============================================================================

  return {
    // Data
    dialogs: useMemo(() => dialogs?.dialogs, [dialogs?.dialogs]),
    dialog,
    dialogDraft,
    isDraft,

    // Status
    getStatus,
    listStatus,
    updateStatus,
    createStatus,
    deleteStatus,
    updateCollectionStatus,

    // Dialog operations
    createDialog,
    createDialogAsync,
    createDraftDialog,
    createCollection,
    updateDialog,
    updateDialogName,
    updateCollection,
    deleteDialog,
    saveDialog,

    // Import/Export
    exportDialog,
    importDialog,

    // Utilities
    hasHandover,
    eventsOptions,
    detailedOptions,
    dialogTags,
    dialogNames,
    randomNode,

    // Variables & Webhooks
    getContextVariables,
    getCustomVariables,
    dialogsByWebhookId,
    findConnectedDialogsForWebhooks,

    // Update utilities
    updateIntentNameOnDialogs,
    updateEntityNameAndValuesOnDialogs,
  };
};

export default useDialogs;
